import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { DropdownOptionContent } from '@frontend/contact-actions-quickfill';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { DropdownField, Stepper, useForm } from '@frontend/design-system';
import { useQuickFillLocationInfo } from '../../../hooks';
import { useLocationTimezone } from '../../../hooks/use-location-timezone';
import { QUICK_FILL_TRACKING_IDS } from '../../../tracking-ids';
import { useQuickFillRecipientShallowStore } from '../store';
import { ComposeMessage } from './compose-message';
import { ComposeMessageStepValue } from './compose-message-step-value';
import { DateTimeContent } from './date-time-content';
import { DateTimeStepValue } from './date-time-step-value';
import { LocationStepValue } from './location-step-value';
import { RecipientsStepValue } from './recipients-step-value';
import { SelectRecipients } from './select-recipients';
import { stepperCardStyle } from './styles';

const trackingIds = QUICK_FILL_TRACKING_IDS.sendMessagesTab;

export type SendMessagesFormConfig = {
  location: {
    type: 'dropdown';
    value: string;
    required: true;
  };
  date1: {
    type: 'date';
    value: string;
    required: true;
    minDate: string;
  };
  date2: {
    type: 'date';
    value: string;
    minDate: string;
  };
  date3: {
    type: 'date';
    value: string;
    minDate: string;
  };
  time1: {
    type: 'time';
    value: string;
    required: true;
  };
  time2: {
    type: 'time';
    value: string;
  };
  time3: {
    type: 'time';
    value: string;
  };
};

export const SendMessagesTabContent = () => {
  const { t } = useTranslation('schedule-quick-fill');
  const { isMultiLocation, selectedLocationIds, getScopeName } = useQuickFillLocationInfo();
  const [appointmentSlotCount, setAppointmentSlotCount] = useState(1);

  const { validLocationIds, selectedIds, clearSelection } = useQuickFillRecipientShallowStore(
    'validLocationIds',
    'selectedIds',
    'clearSelection'
  );

  const hasSingleLocation = validLocationIds.length === 1;

  const form = useForm({
    fields: {
      location: {
        type: 'dropdown',
        value: hasSingleLocation ? validLocationIds[0] : '',
        required: true,
      },
      date1: {
        type: 'date',
        value: '',
        required: true,
        minDate: dayjs().format('YYYY-MM-DD'),
      },
      date2: {
        type: 'date',
        value: '',
        minDate: dayjs().format('YYYY-MM-DD'),
      },
      date3: {
        type: 'date',
        value: '',
        minDate: dayjs().format('YYYY-MM-DD'),
      },
      time1: {
        type: 'time',
        value: '',
        required: true,
      },
      time2: {
        type: 'time',
        value: '',
      },
      time3: {
        type: 'time',
        value: '',
      },
    },
  });

  const { getLocationTimezoneAbbr } = useLocationTimezone();
  const timezoneAbbr = getLocationTimezoneAbbr(form.values.location || '');

  // Seed location value as validLocationIds is not always available on initial render due to async data fetching
  useEffect(() => {
    if (hasSingleLocation) {
      form.seedValues({ location: validLocationIds[0] });
    }
  }, [form, hasSingleLocation, validLocationIds]);

  // Clear recipient selection on form location change
  useEffect(() => {
    if (form.values.location) {
      clearSelection();
    }
  }, [clearSelection, form.values.location]);

  // Clear recipient selection on unmount
  useEffect(() => clearSelection, [clearSelection]);

  return (
    <Stepper>
      {isMultiLocation && (
        <Stepper.Card
          css={stepperCardStyle}
          data-trackingid={trackingIds.locationSelectionStep.stepCard}
          stepValue={<LocationStepValue locationName={getScopeName(form.values.location ?? '')} />}
        >
          <Stepper.Title icon={<Icon name='location' />}>{t('Select Location')}</Stepper.Title>
          <Stepper.Content>
            <DropdownField
              {...form.getFieldProps('location')}
              label={t('Select Location')}
              data-trackingid={trackingIds.locationSelectionStep.locationDropdown}
            >
              {selectedLocationIds.map((locationId) => {
                const hasQuickFillAccess = validLocationIds.includes(locationId);
                return (
                  <DropdownField.Option key={locationId} value={locationId} disabled={!hasQuickFillAccess}>
                    <DropdownOptionContent locationId={locationId} hasQuickFillAccess={hasQuickFillAccess} />
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
          </Stepper.Content>
          <Stepper.ButtonBar>
            <Stepper.NextButton
              trackingId={trackingIds.locationSelectionStep.nextButton}
              isValid={!!form.values.location}
            >
              {t('Next')}
            </Stepper.NextButton>
          </Stepper.ButtonBar>
        </Stepper.Card>
      )}
      <Stepper.Card
        css={stepperCardStyle}
        data-trackingid={trackingIds.dateTimeSelectionStep.stepCard}
        stepValue={
          <DateTimeStepValue
            date={form.values.date1}
            time={form.values.time1}
            timezoneAbbr={timezoneAbbr}
            appointmentSlotCount={appointmentSlotCount}
          />
        }
      >
        <Stepper.Title icon={<Icon name='calendar' />}>{t('Select Date & Time')}</Stepper.Title>
        <Stepper.Content>
          <DateTimeContent
            form={form}
            timezoneAbbr={timezoneAbbr}
            appointmentSlotCount={appointmentSlotCount}
            setAppointmentSlotCount={setAppointmentSlotCount}
          />
        </Stepper.Content>
      </Stepper.Card>
      <Stepper.Card
        css={stepperCardStyle}
        data-trackingid={trackingIds.selectRecipientStep.stepCard}
        stepValue={<RecipientsStepValue count={selectedIds?.length} />}
      >
        <Stepper.Title icon={<Icon name='assign-user' />}>{t('Select Recipients')}</Stepper.Title>
        <SelectRecipients locationId={form.values.location || ''} />
      </Stepper.Card>
      <Stepper.Card
        css={stepperCardStyle}
        data-trackingid={trackingIds.composeMessageStep.stepCard}
        stepValue={<ComposeMessageStepValue />}
      >
        <Stepper.Title icon={<Icon name='messaging' />}>{t('Compose Message')}</Stepper.Title>
        <Stepper.Content>
          <ComposeMessage formValues={form.values} appointmentSlotCount={appointmentSlotCount} />
        </Stepper.Content>
      </Stepper.Card>
    </Stepper>
  );
};
